import {
  getScheduled,
  getCapacity,
  getBacklogChargeValueByRoleName,
  getPredictedChargeValueByRoleName,
  findTotalVolumeFromDemand,
  resolveStoredIsUnitsMode,
  SSD_PLANNING_CHARGE_VOLUME_SOURCES
} from './commonUtils';
import { chainWalk } from '../../../../../../utils/helpers';
import { SSD_VOLUME_SOURCES } from '../../../../../../utils/constants';

export const getSinglesVolume = (outboundVolume, singlesEnabled) => {
  const predictedCharge = getPredictedChargeValueByRoleName(outboundVolume, 'singles');
  const backlogCharge = getBacklogChargeValueByRoleName(outboundVolume, 'singles');
  const singlesPlanningChargeOverride = chainWalk(
    () => outboundVolume.overrides.singles.overrideKeyValueMap.singlesPlanningCharge,
    null
  );
  const singlesPredictedCharge = singlesEnabled ? predictedCharge : 0;
  const singlesPlanningCharge = singlesEnabled
    ? singlesPlanningChargeOverride == null
      ? singlesPredictedCharge
      : parseFloat(singlesPlanningChargeOverride || 0)
    : 0;
  const singlesRodeoBacklog = singlesEnabled ? backlogCharge : 0;
  return { singlesPredictedCharge, singlesPlanningCharge, singlesRodeoBacklog };
};

export const getPick2RebinVolume = (outboundVolume, singlesEnabled, isUnitsOverrideUiEnabled = false) => {
  const { vnaPredictedCharge } = getVnaVolume(outboundVolume);
  const { singlesPlanningCharge } = getSinglesVolume(outboundVolume, singlesEnabled);
  const atropsOverrideCapacityPercentage = chainWalk(
    () => outboundVolume.overrides.pick2rebin.overrideKeyValueMap.atropsOverrideCapacityPercentage,
    null
  );
  // Interpret the stored override with its per-override value mode (falls back to the global units
  // flag for legacy overrides) so a % override is not read as absolute units.
  const isUnitsMode = resolveStoredIsUnitsMode(
    chainWalk(() => outboundVolume.overrides.pick2rebin.overrideKeyValueMap, null),
    isUnitsOverrideUiEnabled
  );
  let pickToRebinPredictedCharge = getPredictedChargeValueByRoleName(outboundVolume, 'picktorebin');
  let pickToRebinMaxPotentialCharge = Math.max(
    getCapacity(outboundVolume) - getScheduled(outboundVolume) - vnaPredictedCharge - singlesPlanningCharge,
    0
  );
  let pickToRebinPlanningCharge = Math.min(pickToRebinPredictedCharge, pickToRebinMaxPotentialCharge);
  pickToRebinPlanningCharge =
    atropsOverrideCapacityPercentage == null
      ? pickToRebinPlanningCharge
      : getPick2RebinOverridePlanningCharge(
          outboundVolume,
          atropsOverrideCapacityPercentage,
          singlesEnabled,
          isUnitsMode
        );
  let pickToRebinRodeoBacklog = getBacklogChargeValueByRoleName(outboundVolume, 'picktorebin');

  return {
    pickToRebinPredictedCharge,
    pickToRebinMaxPotentialCharge,
    pickToRebinPlanningCharge,
    pickToRebinRodeoBacklog
  };
};

export const getPick2RebinOverridePlanningCharge = (
  outboundVolume,
  atropsOverrideCapacityPercentage,
  singlesEnabled,
  isUnitsOverrideUiEnabled = false
) => {
  const { vnaPredictedCharge } = getVnaVolume(outboundVolume);
  const { singlesPlanningCharge } = getSinglesVolume(outboundVolume, singlesEnabled);
  const overrideVolumeUnits = isUnitsOverrideUiEnabled
    ? parseFloat(atropsOverrideCapacityPercentage || 0)
    : Math.round((parseFloat(atropsOverrideCapacityPercentage || 0) * getCapacity(outboundVolume)) / 100);
  return Math.max(overrideVolumeUnits - getScheduled(outboundVolume) - vnaPredictedCharge - singlesPlanningCharge, 0);
};

export const getSortableVolume = (outboundVolume, singlesEnabled, isUnitsOverrideUiEnabled = false) => {
  const { singlesPredictedCharge, singlesPlanningCharge } = getSinglesVolume(outboundVolume, singlesEnabled);
  const { pickToRebinPredictedCharge, pickToRebinMaxPotentialCharge, pickToRebinPlanningCharge } = getPick2RebinVolume(
    outboundVolume,
    singlesEnabled,
    isUnitsOverrideUiEnabled
  );
  let sortablePredictedCharge = pickToRebinPredictedCharge + singlesPredictedCharge;
  let sortableMaxPotentialCharge = pickToRebinMaxPotentialCharge + singlesPlanningCharge;
  let sortablePlanningCharge = pickToRebinPlanningCharge + singlesPlanningCharge;
  return { sortablePredictedCharge, sortableMaxPotentialCharge, sortablePlanningCharge };
};

export const getTotalVolume = (outboundVolume, singlesEnabled, isUnitsOverrideUiEnabled = false) => {
  const { vnaPredictedCharge, vnaRodeoBacklog } = getVnaVolume(outboundVolume);
  const { singlesRodeoBacklog } = getSinglesVolume(outboundVolume, singlesEnabled);
  const { pickToRebinRodeoBacklog } = getPick2RebinVolume(outboundVolume, singlesEnabled, isUnitsOverrideUiEnabled);
  const { sortablePlanningCharge } = getSortableVolume(outboundVolume, singlesEnabled, isUnitsOverrideUiEnabled);
  let totalRodeoBacklog = pickToRebinRodeoBacklog + vnaRodeoBacklog + singlesRodeoBacklog;
  let totalPlanningVolume = totalRodeoBacklog + sortablePlanningCharge + vnaPredictedCharge;
  return { totalRodeoBacklog, totalPlanningVolume };
};

export const getSinglesOverrideGuardrails = (outboundVolume, isUnitsOverrideUiEnabled = false) => {
  const { sortableMaxPotentialCharge } = getSortableVolume(outboundVolume, true, isUnitsOverrideUiEnabled);
  const { pickToRebinPlanningCharge } = getPick2RebinVolume(outboundVolume, true, isUnitsOverrideUiEnabled);
  const minVolume = 0;
  const maxVolume = Math.floor(sortableMaxPotentialCharge - pickToRebinPlanningCharge * 0.75);
  return { minVolume, maxVolume };
};

export const getVnaVolume = (outboundVolume) => {
  const vnaPredictedCharge = getPredictedChargeValueByRoleName(outboundVolume, 'vnapick');
  const vnaRodeoBacklog = getBacklogChargeValueByRoleName(outboundVolume, 'vnapick');

  return { vnaPredictedCharge, vnaRodeoBacklog };
};

export const getCoreVolumesForOverrides = (outboundVolume, singlesEnabled, isUnitsOverrideUiEnabled = false) => {
  return {
    singles: getSinglesVolume(outboundVolume, singlesEnabled),
    vna: getVnaVolume(outboundVolume),
    pick2Rebin: getPick2RebinVolume(outboundVolume, singlesEnabled, isUnitsOverrideUiEnabled),
    sortable: getSortableVolume(outboundVolume, singlesEnabled, isUnitsOverrideUiEnabled),
    total: getTotalVolume(outboundVolume, singlesEnabled, isUnitsOverrideUiEnabled),
    singlesOverrideGuardrails: getSinglesOverrideGuardrails(outboundVolume, isUnitsOverrideUiEnabled)
  };
};

export const getCoreVolumesFromDemand = (demand, cptDateTimeLocal, outboundVolume, singlesEnabled) => {
  let { pickToRebinMaxPotentialCharge } = getPick2RebinVolume(outboundVolume, singlesEnabled);
  const { singlesPredictedCharge, singlesPlanningCharge, singlesRodeoBacklog } = getSinglesVolume(
    outboundVolume,
    singlesEnabled
  );
  const vnaRodeoBacklog = findTotalVolumeFromDemand(
    demand,
    cptDateTimeLocal,
    ['outbound'],
    ['vnapick', 'vnapack'],
    [SSD_VOLUME_SOURCES.RODEO]
  );
  const pickToRebinRodeoBacklog = findTotalVolumeFromDemand(
    demand,
    cptDateTimeLocal,
    ['outbound'],
    ['pick2rebinpick', 'pick2rebinpack'],
    [SSD_VOLUME_SOURCES.RODEO]
  );
  const totalRodeoBacklog = vnaRodeoBacklog + pickToRebinRodeoBacklog;
  const pickToRebinPredictedCharge = findTotalVolumeFromDemand(
    demand,
    cptDateTimeLocal,
    ['outbound'],
    ['pick2rebinpick', 'pick2rebinpack'],
    [SSD_VOLUME_SOURCES.AFT_CHARGE, SSD_VOLUME_SOURCES.DDP_FORECAST, SSD_VOLUME_SOURCES.SNOP]
  );
  const sortablePredictedCharge = pickToRebinPredictedCharge;
  const vnaPredictedCharge = findTotalVolumeFromDemand(
    demand,
    cptDateTimeLocal,
    ['outbound'],
    ['vnapick', 'vnapack'],
    [SSD_VOLUME_SOURCES.AFT_CHARGE, SSD_VOLUME_SOURCES.DDP_FORECAST]
  );
  const sortablePlanningCharge = findTotalVolumeFromDemand(
    demand,
    cptDateTimeLocal,
    ['outbound'],
    ['pick2rebinpick', 'pick2rebinpack'],
    SSD_PLANNING_CHARGE_VOLUME_SOURCES
  );
  const pickToRebinPlanningCharge = sortablePlanningCharge;

  const totalPlanningVolume = totalRodeoBacklog + sortablePlanningCharge + vnaPredictedCharge;

  let sortableMaxPotentialCharge;
  ({ sortableMaxPotentialCharge } = getSortableVolume(outboundVolume, singlesEnabled));

  const { minVolume, maxVolume } = getSinglesOverrideGuardrails(outboundVolume);
  return {
    singles: { singlesPredictedCharge, singlesPlanningCharge, singlesRodeoBacklog },
    vna: { vnaPredictedCharge, vnaRodeoBacklog },
    pick2Rebin: {
      pickToRebinPredictedCharge,
      pickToRebinMaxPotentialCharge,
      pickToRebinPlanningCharge,
      pickToRebinRodeoBacklog
    },
    sortable: {
      sortablePredictedCharge,
      sortableMaxPotentialCharge,
      sortablePlanningCharge
    },
    total: {
      totalRodeoBacklog,
      totalPlanningVolume
    },
    singlesOverrideGuardrails: { minVolume, maxVolume }
  };
};
